/**
 * Structure package PF for a pay month — same mid-month segment rules as Process Payroll
 * (full-month paid days = calendar days; no LOP). Used so Profile SA balances on Sep
 * prorated employer PF when ceiling changes mid-month (e.g. 17-Sep-2026).
 */
import { resolveRatesSectionForDate } from "./statutoryRatesDefaults";

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

function roundRupee(n) {
  return Math.round(Number(n || 0));
}

function roundAmount(n) {
  return Math.round(Number(n || 0));
}

function normalizePfFields(pf = {}) {
  const pfWageCeiling = Number(pf.pfWageCeiling ?? pf.ceiling ?? 15000) || 15000;
  return {
    ...pf,
    employeeShare: Number(pf.employeeShare ?? 12) || 12,
    employerShare: Number(pf.employerShare ?? 12) || 12,
    pfWageCeiling,
    ceiling: pfWageCeiling,
    epsWageCeiling: Number(pf.epsWageCeiling ?? 15000) || 15000,
    edliWageCeiling: Number(pf.edliWageCeiling ?? 15000) || 15000,
    eps: Number(pf.eps ?? 8.33) || 8.33,
    adminCharges: Number(pf.adminCharges ?? 0.5) || 0.5,
    edliRate: Number(pf.edliRate ?? 0.5) || 0.5,
    prorateCeiling:
      pf.prorateCeiling === true ||
      pf.prorateCeiling === 1 ||
      pf.prorateCeiling === "true" ||
      pf.prorateCeiling === "yes",
  };
}

function isProrateFlag(v) {
  return v === true || v === 1 || v === "true" || v === "yes";
}

function isoDayUtc(iso) {
  const s = String(iso).slice(0, 10);
  return Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
}

function findHistoryMatchDate(sectionHistory, effectiveDate) {
  if (!effectiveDate || !Array.isArray(sectionHistory) || !sectionHistory.length) return null;
  const target = String(effectiveDate).slice(0, 10);
  const sorted = [...sectionHistory].sort((a, b) => new Date(b.date) - new Date(a.date));
  const match = sorted.find(
    (entry) => entry.active !== false && String(entry.date).slice(0, 10) <= target
  );
  return match?.date ? String(match.date).slice(0, 10) : null;
}

function parsePayMonth(payMonth) {
  const ym = String(payMonth || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(ym)) return null;
  const [y, m] = ym.split("-").map(Number);
  const monthStart = `${ym}-01`;
  const last = new Date(y, m, 0).getDate();
  const monthEnd = `${ym}-${String(last).padStart(2, "0")}`;
  return { ym, y, m, monthStart, monthEnd, calendarDays: last };
}

/** Mirror backend resolvePfRateSegments. */
export function resolvePfRateSegments(configJson, payMonth) {
  const period = parsePayMonth(payMonth);
  if (!period) return [];
  const cfg = configJson || {};
  const history = cfg.history?.pf;
  const basePf = normalizePfFields({ ...(cfg.rates?.pf || {}) });

  const starts = new Set([period.monthStart]);
  (Array.isArray(history) ? history : []).forEach((entry) => {
    if (entry?.active === false) return;
    const d = String(entry?.date || "").slice(0, 10);
    if (d > period.monthStart && d <= period.monthEnd) starts.add(d);
  });
  const sorted = [...starts].sort();
  const dayMs = 24 * 3600 * 1000;

  return sorted.map((from, i) => {
    const to =
      i + 1 < sorted.length
        ? new Date(isoDayUtc(sorted[i + 1]) - dayMs).toISOString().slice(0, 10)
        : period.monthEnd;
    return {
      from,
      to,
      days: Math.round((isoDayUtc(to) - isoDayUtc(from)) / dayMs) + 1,
      payableDays: Math.round((isoDayUtc(to) - isoDayUtc(from)) / dayMs) + 1,
      effectiveFrom: findHistoryMatchDate(history, from),
      rates: normalizePfFields(resolveRatesSectionForDate(history, basePf, from) || basePf),
    };
  });
}

function computePfSegmentWages({
  actualPfWage,
  segments = [],
  calendarDays,
  paidDays,
  ceilingPf = true,
} = {}) {
  const actual = Math.max(0, Number(actualPfWage) || 0);
  const cal = Number(calendarDays) || 0;
  const totalPaid = Math.max(0, Number(paidDays) || 0);
  const single = segments.length === 1;
  const totalSegDays = segments.reduce((s, g) => s + (Number(g.days) || 0), 0);

  const out = segments.map((seg) => {
    const rates = seg.rates || {};
    const prorate = isProrateFlag(rates.prorateCeiling);
    const segPaid = Math.max(0, Number(seg.payableDays) || 0);
    const segDays = Number(seg.days) || 0;
    const zeroed = prorate && segPaid <= 0;
    const basisDays = prorate ? segPaid : segDays;
    const scale = (monthly) => {
      if (zeroed) return 0;
      if (cal <= 0 || basisDays === cal) return monthly;
      return (monthly / cal) * basisDays;
    };

    let share = actual;
    if (!single) {
      if (totalPaid > 0) share = (actual * segPaid) / totalPaid;
      else share = totalSegDays > 0 ? (actual * segDays) / totalSegDays : 0;
    }

    const monthlyCeiling = Number(rates.pfWageCeiling ?? rates.ceiling ?? 15000) || 15000;
    const calculatedCeiling = scale(monthlyCeiling);
    const epsCeiling = scale(Number(rates.epsWageCeiling ?? 15000) || 15000);
    const edliCeiling = scale(Number(rates.edliWageCeiling ?? 15000) || 15000);

    return {
      days: segDays,
      payableDays: segPaid,
      prorateCeiling: prorate,
      monthlyCeiling,
      calculatedCeiling,
      wageShare: round2(share),
      eligibleWage: zeroed ? 0 : round2(ceilingPf ? Math.min(share, calculatedCeiling) : share),
      epsCeiling,
      epsWage: zeroed ? 0 : Math.min(share, epsCeiling),
      edliCeiling,
      edliWage: zeroed ? 0 : Math.min(share, edliCeiling),
      rates,
    };
  });

  return { segments: out };
}

/**
 * Full-month structure package PF for payMonth (attendance = full calendar days).
 * @returns {{ employee: number, employer: number, segmentCount: number } | null}
 */
export function computeStructurePfForPayMonth({
  pfWageMonthly,
  statutoryConfigJson,
  payMonth,
  applicability = {},
} = {}) {
  if (!statutoryConfigJson || !payMonth) return null;
  if (applicability.pfApplicable === false) {
    return { employee: 0, employer: 0, segmentCount: 0 };
  }

  const period = parsePayMonth(payMonth);
  if (!period) return null;
  const actual = Math.max(0, Number(pfWageMonthly) || 0);
  if (actual <= 0) return { employee: 0, employer: 0, segmentCount: 0 };

  const ceilingPf = applicability.ceilingPf !== false;
  const epsApplicable = applicability.epsApplicable === true;
  const rawSegs = resolvePfRateSegments(statutoryConfigJson, period.ym);
  if (!rawSegs.length) return null;

  const cal = period.calendarDays;
  const pfSeg = computePfSegmentWages({
    actualPfWage: actual,
    segments: rawSegs,
    calendarDays: cal,
    paidDays: cal,
    ceilingPf,
  });
  const segs = pfSeg.segments || [];

  let employerShare12 = 0;
  let adminCharges = 0;
  let edli = 0;
  let employee = 0;

  if (segs.length > 1) {
    employerShare12 = roundRupee(
      segs.reduce((s, g) => s + g.eligibleWage * (Number(g.rates.employerShare) / 100), 0)
    );
    adminCharges = round2(
      segs.reduce((s, g) => s + g.eligibleWage * (Number(g.rates.adminCharges) / 100), 0)
    );
    const edliRaw = round2(
      segs.reduce((s, g) => s + g.edliWage * (Number(g.rates.edliRate) / 100), 0)
    );
    const edliCap = round2(
      segs.reduce((s, g) => s + g.edliCeiling * (Number(g.rates.edliRate) / 100), 0)
    );
    edli = Math.min(edliRaw, edliCap);
    employee = roundRupee(
      segs.reduce((s, g) => s + g.eligibleWage * (Number(g.rates.employeeShare) / 100), 0)
    );
  } else {
    const rates = segs[0]?.rates || {};
    const wage = segs[0]?.eligibleWage ?? 0;
    const empPct = Number(rates.employeeShare ?? 12) / 100;
    const emprPct = Number(rates.employerShare ?? 12) / 100;
    const adminPct = Number(rates.adminCharges ?? 0.5) / 100;
    const edliPct = Number(rates.edliRate ?? 0.5) / 100;
    employee = roundAmount(wage * empPct);
    employerShare12 = roundRupee(wage * emprPct);
    adminCharges = round2(wage * adminPct);
    // CTC package: EDLI on eligible PF wage (full-month 25k → 3250 with 12+0.5+0.5)
    edli = round2(wage * edliPct);
  }

  void epsApplicable;

  return {
    employee: round2(employee),
    employer: round2(employerShare12 + adminCharges + edli),
    segmentCount: segs.length,
  };
}
